import { SessionState, SessionType } from "constants/enums";
import { ListModel, TranslationUDF } from "shared/interfaces/core-interfaces";

// Request Types
export interface BaseRequest {
    locationId: number;
    timeZone: string;
}

export interface StartSessionRequest extends BaseRequest {
    projectId?: string;
}

export interface StopSessionRequest extends BaseRequest {
}

export interface BreakSessionRequest extends BaseRequest {
}

export interface InsertSessionRequest extends BaseRequest {
    employeeId?: string;
    tenantId?: string;
    startLocal: string;
    endLocal: string;
    type: number;
}

export interface UpdateSessionRequest extends InsertSessionRequest {
    id: string;
}

export interface MonthlyOverviewRequest {
    timeZone: string;
    employeeId?: string;
    tenantId?: string;
    month?: number;
    year?: number;
}

// Response Types
export interface MonthToDateSummery {
    target: string;
    current: string;
    difference: string;
    targetIndustrial: number;
    currentIndustrial: number;
    differenceIndustrial: number;
    remainingVacationDays: number;
    usedVacationDays: number;
}

export interface BreakTimeSummery {
    paidBreakTime: string;
    unpaidBreakTime: string;
    paidBreakTimeIndustrial: number;
    unpaidBreakTimeIndustrial: number;

    breakTimeHelpers: BreakTimeHelper[]
}

export interface BreakTimeHelper {
    from: Date;
    to: Date;
    paidBreakTime: number;
}

export interface OvertimePool {
    availableOvertime: string;
    untilLastMonth: string;
    currentMonthOvertime: string;
    requestedTimeCompensation: string;
    availableOvertimeIndustrial: number;
    untilLastMonthIndustrial: number;
    currentMonthOvertimeIndustrial: number;
    requestedTimeCompensationIndustrial: number;
}

export interface OvertimeSummery {
    regularOverTime: string;
    additionalOverTime: string;
    workOnHoliday: string;
    workAtNight: string;
    workOnDaysOff: string;
    workOnLeave: string;
    totalOvertime: string;
    availableOvertime: string;
    unfinished: string;
    requestedOvertime: string;

    regularOverTimeIndustrial: number;
    additionalOverTimeIndustrial: number;
    workOnHolidayIndustrial: number;
    workAtNightIndustrial: number;
    workOnDaysOffIndustrial: number;
    workOnLeaveIndustrial: number;
    totalOvertimeIndustrial: number;
    availableOvertimeIndustrial: number;
    unfinishedIndustrial: number;
    requestedOvertimeIndustrial: number;
}

export interface CumulativeAbsenceHours {
    absenceTypeId: string;
    defaultLanguage: string;
    names: TranslationUDF[]
    approvedTotal: string;
    pendingTotal: string;
    approvedTotalIndustrial: number;
    pendingTotalIndustrial: number;
}

export interface FullMonthlyRecapResponse {
    monthToDateSummery: MonthToDateSummery;
    breakTimeSummery: BreakTimeSummery;
    overtimePool: OvertimePool;
    overtimeSummery: OvertimeSummery;
    cumulativeAbsenceHoursPerType: CumulativeAbsenceHours[];
}

export interface WorkTimeSummery {
    from: string;
    to: string;
    monday: number;
    tuesday: number;
    wednesday: number;
    thursday: number;
    friday: number;
    saturday: number;
    sunday: number;
    paidBreakTime: number;
}

export interface SessionBaseResponse {
    start?: string;
    startOriginal?: string;
    startTimeZoneOriginal?: string;
    end?: string;
    endOriginal?: string;
    endTimeZoneOriginal?: string;
    duration: string;
    type: SessionType;
    readOnly: boolean;
}

export interface WorkSessionResponse extends SessionBaseResponse {
    id: string;
    start: string;
    startOriginal: string;
    startTimeZoneOriginal: string;
    startEdited: boolean;
    endEdited: boolean;
    sessionLocked: boolean;
    sessionType: "work";
    locationId: number;
    location: string;
    costCenterTrackings: GetCostCenterTrackingResponse[];
}

export interface HolidaySessionResponse extends SessionBaseResponse {
    date: string;
    holiday: string;
    holidayType: string;
    holidayTypeId: number;
    sessionType: "holiday";
}

export interface AbsenceSessionResponse extends SessionBaseResponse {
    date: string;
    isApproved: boolean;
    absenceTypeId: string;
    /** The type's name in its default language; `names` has it in every language it is translated to. */
    absenceType: string;
    defaultLanguage: string;
    names: TranslationUDF[];
    isHoursBased: boolean;
    sessionType: "absence";
}

export type SessionResponse =
    | WorkSessionResponse
    | AbsenceSessionResponse
    | HolidaySessionResponse;

export interface DailySessionsOverview {
    dayOfTheWeek: string;
    calendarWeek: number;
    target: string;
    current: string;
    difference: string;
    targetIndustrial: number;
    currentIndustrial: number;
    currentBreakIndustrial: number;
    differenceIndustrial: number;
    sessions: SessionResponse[];
}

export interface MonthlyTrackingResponse {
    monthToDateSummery: MonthToDateSummery;
    overtimeSummery: OvertimeSummery;
    worktimeSummeries: WorkTimeSummery[];
    dailyOverview: Record<string, DailySessionsOverview>;
    costCentersThisMonth?: GetCostCenterTrackingsByProjectDurationResponse | null;
    isLocked: boolean;
}

export interface StateResponse {
    hubToken: string;
    state: SessionState;
    startedAt: Date;
    tenantId: string;
    tenantName: string;
    tenantAbbreviation: string;
    hasEmployment: boolean;
    settings: TimeTrackingSettingsResponse;
}

export interface TimeTrackingSettingsResponse {
    id: string;
    employeeId: string;
    projectId?: string;
    project: string;
    locationId: number;
    location: string;
}

export interface TimeTrackingDataResponse {
    sessionTypes: ListModel<number>[];
    locations: ListModel<number>[];
    projects: ListModel<string>[];
}

// State
export interface TimeTrackingState {
    monthlySession: MonthlyTrackingResponse | null;
    monthlyRecap: FullMonthlyRecapResponse | null;
    sessionState: StateResponse | null;
    hasSessionState: boolean;
    data?: TimeTrackingDataResponse | null;
    /** The month/tenant/employee whose sessions are currently on screen, or null when no
     *  session-assignments view is mounted. Anything that refreshes that view must read this
     *  instead of falling back to the logged-in user's current month. */
    viewedMonth: { month: number; year: number } | null;
    viewedEmployeeId: string | null;
    viewedTenantId: string | null;
    monthClosings: MonthClosingResponse[] | null;
    filterData: FilterDataResponse | null;
    projectPlanning: ProjectPlanningResponse | null;
    projectPlanningRequestId: string | null;
    /** Project the current planning state describes — the id the in-flight or finished fetch was
     *  made for. A null `projectPlanning` alone can't say whether the project has no phases or
     *  whether its tree simply isn't in yet, and callers must not confuse the two. */
    projectPlanningProjectId: string | null;
    projectPlanningStatus: ProjectPlanningStatus;
}

/** "loaded" is the only state in which `projectPlanning` answers "does this project have phases". */
export type ProjectPlanningStatus = "idle" | "loading" | "loaded" | "failed";

export interface ProjectPlanningResponse {
    id: string;
    name: string;
    description: string | null;
    projectId: string | null;
    project: string | null;
    sourceTemplateId: string | null;
    phaseNodes: ProjectPlanningPhaseNode[];
}

export interface ProjectPlanningPhaseNode {
    id: string;
    parentNodeId: string | null;
    name: string;
    description: string | null;
    sortOrder: number;
    depth: number;
    /** Set only on the node that was archived directly. */
    isArchived: boolean;
    /** Set when this node or any node above it is archived — these can never be selected. */
    isArchivedEffective: boolean;
    archivedAtUnix: number | null;
    childNodes: ProjectPlanningPhaseNode[];
}

export const initialTimeTrackingState: TimeTrackingState = {
    monthlySession: null,
    monthlyRecap: null,
    sessionState: null,
    hasSessionState: false,
    data: null,
    viewedMonth: null,
    viewedEmployeeId: null,
    viewedTenantId: null,
    monthClosings: null,
    filterData: null,
    projectPlanning: null,
    projectPlanningRequestId: null,
    projectPlanningProjectId: null,
    projectPlanningStatus: "idle"
};

// Initial Requests
export const initInsertSessionRequest: InsertSessionRequest = {
    timeZone: '',
    employeeId: undefined,
    tenantId: undefined,
    startLocal: '',
    endLocal: '',
    type: 0,
    locationId: 0
};

// #region CostCenterTracking

export interface CreateCostCenterTrackingRequest {
    timeTrackingSessionId: string;
    projectId: string;
    date: string;
    description?: string;

    phaseNodeIds?: string[];
}

export interface UpdateCostCenterTrackingRequest {
    costTrackingId: string;
    projectId: string;
    date: string;
    description?: string;

    phaseNodeIds?: string[];
}

export interface SelectedNodeDto {
    id: string;
    name: string;
    selectedChild: SelectedNodeDto | null;
}

export interface SelectedRootDto {
    rootId: string;
    rootName: string;
    rootSortOrder: number;
    selectedChild: SelectedNodeDto;
}

export interface GetCostCenterTrackingResponse {
    costCenterTrackingId: string;
    projectPrefix: string;
    projectName: string;
    projectFullName: string;
    projectId: string;
    duration: number;
    description?: string;
    selections: SelectedRootDto[];
    timeTrackingSessionId: string;
}

export interface GetLatestCostCenterTrackingResponse {
    description: string;
    phaseNodeIds: string[];
}

// #endregion

export interface GetCostCenterTrackingProjectDateResponse {
    projectPrefix: string;
    projectName: string;
    projectFullName: string;
    duration: number;
}

export type GetCostCenterTrackingsByProjectDurationResponse = Record<string, GetCostCenterTrackingProjectDateResponse>;

// #region MonthClosing

export interface MonthClosingResponse {
    id: string;
    tenantId: string;
    tenant: string;
    employeeId: string;
    employee: string;
    closedById: string | null;
    closedBy: string | null;
    closedAt: string | null;
    isClosed: boolean;
    hasPendingReopenRequest: boolean;
    month: string; // Format: "yyyy, MMMM" e.g. "2025, December"
}

export interface CreateMonthClosingRequest {
    employeeId: string;
    tenantId: string;
    year: number;
    month: number;
}

export interface GetMonthClosingsRequest {
    employeeId?: string;
    tenantId?: string;
}

export interface CreateMonthReopenRequestRequest {
    id: string;
    description?: string;
    employeeId: string;
}

export interface FilterDataResponse {
    tenants: ListModel<string>[];
    employees: Record<string, ListModel<string>[]>;
}

// #endregion

// #region Phase

export interface CreatePhaseRequest {
    projectId?: string;
    parentPhaseId?: string;
    phaseName: string;
    description?: string;
}

export interface UpdatePhaseRequest {
    phaseId: string;
    projectId?: string;
    parentPhaseId?: string;
    phaseName: string;
    description?: string;
}

export interface GetPhaseResponse {
    projectId?: string;
    parentPhaseId?: string;
    phaseName: string;
    description?: string;
}

// #endregion
